import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { Helmet } from "react-helmet";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import MUIDataTable from "mui-datatables";
import Axios from "../axios";

const isShopToolDebugEnabled = false;

function debugShopTool(step, payload) {
  if (!isShopToolDebugEnabled) {
    return;
  }
  // eslint-disable-next-line no-console
  console.info(`[ShopTool] ${step}`, payload);
}

const MEMBER_TYPE_OPTIONS = [
  { label: "All", value: "all" },
  { label: "Leisure", value: "leisure" },
  { label: "Business", value: "business" },
  { label: "Corporate", value: "corporate" },
  { label: "Affiliate", value: "affiliate" },
];

const REFUNDABLE_FILTER_OPTIONS = [
  { label: "All", value: "all" },
  { label: "Refundable Only", value: "refundable" },
  { label: "Non-Refundable Only", value: "non-refundable" },
];

const DASHBOARD_CONTROL_SX = {
  "& .MuiInputLabel-root": {
    fontSize: "1.4rem",
    fontWeight: 500,
  },
  "& .MuiInputBase-input": {
    fontSize: "1.6rem",
  },
  "& .MuiSelect-select": {
    fontSize: "1.6rem",
  },
};

const RESULT_FILTER_CONTROL_SX = {
  minWidth: 220,
  maxWidth: 280,
  "& .MuiInputLabel-root": {
    fontSize: "1.15rem",
    fontWeight: 500,
  },
  "& .MuiSelect-select": {
    fontSize: "1.3rem",
    minHeight: "38px",
    display: "flex",
    alignItems: "center",
  },
};

function formatDate(dateLike) {
  const date = new Date(dateLike);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getTomorrowAndNextDay() {
  const now = new Date();
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);

  const dayAfterTomorrow = new Date(now);
  dayAfterTomorrow.setDate(now.getDate() + 2);

  return {
    checkIn: formatDate(tomorrow),
    checkOut: formatDate(dayAfterTomorrow),
  };
}

function buildAutosuggestLabel(option) {
  if (!option) {
    return "";
  }
  const type = option.type ? ` (${option.type})` : "";
  return `${option.fullName || option.name || ""}${type}`.trim();
}

function toSafeNumber(value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string") {
    const cleaned = value.replace(/[^0-9.-]/g, "");
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

function formatMoney(value) {
  const parsed = toSafeNumber(value);
  if (parsed === null) {
    return "-";
  }
  return parsed.toFixed(2);
}

function uniqueValues(values) {
  const seen = new Set();
  const out = [];
  values.forEach((value) => {
    const key = String(value || "").trim();
    if (!key || seen.has(key.toLowerCase())) {
      return;
    }
    seen.add(key.toLowerCase());
    out.push(key);
  });
  return out;
}

function formatRefundableValue(value) {
  const parsed = parseRefundable(value);
  if (parsed === true) return "Y";
  if (parsed === false) return "N";
  return "-";
}

function parseRefundable(value) {
  if (value === true) {
    return true;
  }
  if (value === false) {
    return false;
  }

  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]/g, "");
  if (["y", "yes", "true", "refundable", "freecancellation"].includes(normalized)) {
    return true;
  }
  if (
    ["n", "no", "false", "nonrefundable", "nonrefund", "notrefundable"].includes(
      normalized,
    )
  ) {
    return false;
  }
  return null;
}

function getLowestRate(rates) {
  if (!Array.isArray(rates) || rates.length === 0) {
    return null;
  }

  let lowest = null;
  let minValue = Number.POSITIVE_INFINITY;

  rates.forEach((rate) => {
    const value = toSafeNumber(rate?.totalRate) ?? toSafeNumber(rate?.baseRate);
    if (value !== null && value < minValue) {
      minValue = value;
      lowest = rate;
    }
  });

  return lowest || rates[0] || null;
}

function filterRatesByRefundable(rates, refundableFilter) {
  if (!Array.isArray(rates)) {
    return [];
  }

  if (refundableFilter === "all") {
    return rates;
  }

  return rates.filter((rate) => {
    const refundableValue = parseRefundable(
      rate?.refundable ?? rate?.refundability ?? rate?.isRefundable,
    );
    if (refundableValue === null) {
      return false;
    }

    if (refundableFilter === "refundable") {
      return refundableValue === true;
    }

    return refundableValue === false;
  });
}

function buildExportRows(tableRows) {
  return (Array.isArray(tableRows) ? tableRows : []).map((row) => ({
    "Hotel ID": row?.id || "-",
    "Hotel Name": row?.name || "-",
    "Check In Date": row?.checkIn || "-",
    "Check Out Date": row?.checkOut || "-",
    "Length of Stay": row?.numberOfDays || "-",
    Occupancy: row?.occupancy || "-",
    Supplier: row?.supplier || "-",
    "Rate Plan Name": row?.ratePlanName || "-",
    "Rate Plan Code": row?.ratePlanCode || "-",
    "Room Type": row?.roomType || "-",
    Subtotal: formatMoney(row?.subtotal),
    Tax: formatMoney(row?.tax),
    Fee: formatMoney(row?.fee),
    Total: formatMoney(row?.total),
    Currency: row?.currency || "USD",
    Refundable: formatRefundableValue(row?.refundable),
  }));
}

function downloadCsv(rows, filename) {
  if (!rows.length) {
    return;
  }

  const headers = Object.keys(rows[0]);
  const escaped = (value) => {
    const str = String(value === null || value === undefined ? "" : value);
    return `"${str.replace(/"/g, '""')}"`;
  };

  const csv = [headers.map(escaped).join(",")]
    .concat(rows.map((row) => headers.map((key) => escaped(row[key])).join(",")))
    .join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function downloadExcel(rows, filename) {
  if (!rows.length) {
    return;
  }

  const worksheet = XLSX.utils.json_to_sheet(rows);
  worksheet["!cols"] = [
    { wch: 19 },
    { wch: 31 },
    { wch: 16 },
    { wch: 16 },
    { wch: 17 },
    { wch: 13 },
    { wch: 21 },
    { wch: 28 },
    { wch: 18 },
    { wch: 26 },
    { wch: 13 },
    { wch: 13 },
    { wch: 13 },
    { wch: 13 },
    { wch: 13 },
    { wch: 13 },
  ];
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Shop Tool");
  XLSX.writeFile(workbook, filename);
}

const ShopTool = () => {
  const defaultDates = useMemo(() => getTomorrowAndNextDay(), []);

  const [memberType, setMemberType] = useState("all");
  const [searchMode, setSearchMode] = useState("location");
  const [checkIn, setCheckIn] = useState(defaultDates.checkIn);
  const [checkOut, setCheckOut] = useState(defaultDates.checkOut);
  const [occupants, setOccupants] = useState(2);
  const [maxHotels, setMaxHotels] = useState(100);

  const [multiDateMode, setMultiDateMode] = useState(false);
  const [dateRanges, setDateRanges] = useState([
    { checkIn: defaultDates.checkIn, checkOut: defaultDates.checkOut },
  ]);

  const [searchTerm, setSearchTerm] = useState("");
  const [locationOptions, setLocationOptions] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [hotelIdInput, setHotelIdInput] = useState("");

  const [loadingAutosuggest, setLoadingAutosuggest] = useState(false);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [shopResult, setShopResult] = useState(null);

  const [refundableFilter, setRefundableFilter] = useState("all");
  const [lowestPriceOnly, setLowestPriceOnly] = useState(false);

  const [jsonDialogOpen, setJsonDialogOpen] = useState(false);
  const [selectedHotelJson, setSelectedHotelJson] = useState(null);

  useEffect(() => {
    if (searchMode !== "location") {
      setLocationOptions([]);
      return undefined;
    }

    const term = String(searchTerm || "").trim();
    if (term.length < 2) {
      setLocationOptions([]);
      return undefined;
    }

    let isCancelled = false;
    const timer = setTimeout(() => {
      setLoadingAutosuggest(true);
      debugShopTool("autosuggest.request", { term });

      Axios.post(`${process.env.REACT_APP_BACKEND}shop-tool-autosuggest`, {
        term,
      })
        .then((response) => {
          if (isCancelled) {
            return;
          }

          const options = Array.isArray(response?.data?.data?.locationSuggestions)
            ? response.data.data.locationSuggestions
            : [];

          setLocationOptions(options);
          debugShopTool("autosuggest.success", {
            term,
            suggestions: options.length,
          });
        })
        .catch((error) => {
          if (isCancelled) {
            return;
          }

          debugShopTool("autosuggest.error", {
            term,
            status: error?.response?.status || null,
            message:
              error?.response?.data?.message ||
              error?.response?.data?.error ||
              error?.message,
          });

          setLocationOptions([]);
          toast.error(
            error?.response?.data?.message ||
              error?.response?.data?.error ||
              "Failed to fetch autosuggest"
          );
        })
        .finally(() => {
          if (!isCancelled) {
            setLoadingAutosuggest(false);
          }
        });
    }, 350);

    return () => {
      isCancelled = true;
      clearTimeout(timer);
    };
  }, [searchMode, searchTerm]);

  const executeShopSearch = () => {
    if (searchMode === "location") {
      if (!selectedLocation?.id || !selectedLocation?.fullName) {
        toast.error("Please select a valid location");
        return;
      }
    } else {
      const parsedHotelIds = hotelIdInput
        .split(",")
        .map((entry) => String(entry || "").trim())
        .filter(Boolean);
      if (parsedHotelIds.length === 0) {
        toast.error("Please provide at least one hotel ID");
        return;
      }
    }

    const ranges = multiDateMode
      ? dateRanges.filter((entry) => entry?.checkIn && entry?.checkOut)
      : [{ checkIn, checkOut }];

    if (ranges.length === 0) {
      toast.error("At least one date range is required");
      return;
    }

    for (let index = 0; index < ranges.length; index += 1) {
      const range = ranges[index];
      if (!range?.checkIn || !range?.checkOut) {
        toast.error(`Please complete date range ${index + 1}`);
        return;
      }

      if (new Date(range.checkOut) <= new Date(range.checkIn)) {
        toast.error(
          ranges.length > 1
            ? `Check-out must be after check-in for date range ${index + 1}`
            : "Check-out date must be after check-in date"
        );
        return;
      }
    }

    const payload = {
      searchMode,
      memberType,
      checkIn,
      checkOut,
      currency: "USD",
      culture: "en-us",
      nationality: "US",
      countryOfResidence: "US",
      occupancies: [{ numOfAdults: Number(occupants) > 0 ? Number(occupants) : 2, childAges: [] }],
      contentFields: ["All"],
      maxHotels: Number(maxHotels) > 0 ? Number(maxHotels) : 100,
      maxRatesPerHotel: 100,
    };

    if (searchMode === "location") {
      payload.location = selectedLocation;
      payload.locationId = selectedLocation.id;
      payload.fullName = selectedLocation.fullName;
    } else {
      payload.hotelIds = hotelIdInput
        .split(",")
        .map((entry) => String(entry || "").trim())
        .filter(Boolean);
    }

    if (multiDateMode) {
      payload.dateRanges = ranges;
    }

    setLoadingSearch(true);
    debugShopTool("search.request", payload);

    Axios.post(`${process.env.REACT_APP_BACKEND}shop-tool-search`, payload)
      .then((response) => {
        const nextResult = response?.data?.data || null;
        setShopResult(nextResult);
        debugShopTool("search.success", {
          searchMode,
          hotelsReturned: nextResult?.hotelsReturned || 0,
          totalHotelsFound: nextResult?.totalHotelsFound || 0,
        });
      })
      .catch((error) => {
        setShopResult(null);
        debugShopTool("search.error", {
          searchMode,
          status: error?.response?.status || null,
          message:
            error?.response?.data?.message ||
            error?.response?.data?.error ||
            error?.message,
          payload: error?.response?.data || null,
        });
        toast.error(
          error?.response?.data?.message ||
            error?.response?.data?.error ||
            "Failed to fetch hotel rates"
        );
      })
      .finally(() => {
        setLoadingSearch(false);
      });
  };

  const hotels = useMemo(
    () => (Array.isArray(shopResult?.hotels) ? shopResult.hotels : []),
    [shopResult]
  );

  const displayHotels = useMemo(() => {
    return hotels
      .map((hotel) => {
        const sourceRates =
          Array.isArray(hotel?.roomsAndRates) && hotel.roomsAndRates.length > 0
            ? hotel.roomsAndRates
            : Array.isArray(hotel?.rates)
              ? hotel.rates
              : [];

        const ratesByRefundable = filterRatesByRefundable(
          sourceRates,
          refundableFilter,
        );

        const finalRates =
          refundableFilter === "all" ? sourceRates : ratesByRefundable;

        const fallbackLowest = hotel?.lowestRoomSummary
          ? {
              roomType: hotel.lowestRoomSummary.roomType || "",
              baseRate: hotel.lowestRoomSummary.baseRate,
              subtotal: hotel.lowestRoomSummary.subtotal,
              tax: hotel.lowestRoomSummary.tax,
              fee: hotel.lowestRoomSummary.fee,
              totalRate: hotel.lowestRoomSummary.totalRate,
              refundable: hotel.lowestRoomSummary.refundable,
            }
          : null;

        const lowestRate =
          getLowestRate(finalRates) || (refundableFilter === "all" ? fallbackLowest : null);

        const supplierNames = uniqueValues(
          [
            ...(Array.isArray(hotel?.suppliers) ? hotel.suppliers : []),
            ...finalRates.map((rate) => rate?.providerName),
            hotel?.provider?.providerName,
          ].filter(Boolean),
        );

        const supplierText =
          lowestRate?.providerName ||
          hotel?.provider?.providerName ||
          (supplierNames.length > 0 ? supplierNames[0] : "-");

        const supplierNetRate = finalRates
          .map((rate) => toSafeNumber(rate?.baseRate) ?? toSafeNumber(rate?.totalRate))
          .filter((value) => value !== null)
          .sort((a, b) => a - b)[0];

        if (refundableFilter !== "all" && finalRates.length === 0 && !lowestRate) {
          return null;
        }

        return {
          ...hotel,
          displayRates: finalRates,
          suppliersText: supplierNames.length > 0 ? supplierNames.join(", ") : "-",
          supplierText,
          supplierNetRate,
          lowestRoomType: lowestRate?.roomType || lowestRate?.name || "-",
          lowestBaseRate: lowestRate?.baseRate,
          lowestSubtotal: lowestRate?.subtotal,
          lowestTax: lowestRate?.tax,
          lowestFee: lowestRate?.fee,
          lowestTotal: lowestRate?.totalRate,
          lowestRefundableText:
            parseRefundable(lowestRate?.refundable) === true
              ? "Yes"
              : parseRefundable(lowestRate?.refundable) === false
                ? "No"
                : "Unknown",
        };
      })
      .filter(Boolean);
  }, [hotels, refundableFilter]);

  const tableRows = useMemo(() => {
    return displayHotels.flatMap((hotel) => {
      const sourceRates = Array.isArray(hotel?.displayRates) ? hotel.displayRates : [];
      const rates = lowestPriceOnly
        ? [getLowestRate(sourceRates)].filter(Boolean)
        : sourceRates;

      if (rates.length === 0) {
        const fallbackRate = hotel?.lowestRoomSummary || null;
        if (!fallbackRate) {
          return [];
        }

        const fallbackRefundable = parseRefundable(fallbackRate?.refundable);
        if (
          (refundableFilter === "refundable" && fallbackRefundable !== true) ||
          (refundableFilter === "non-refundable" && fallbackRefundable !== false)
        ) {
          return [];
        }

        return [
          {
            heroImage: hotel?.heroImage || "",
            id: hotel?.id || "-",
            name: hotel?.name || "-",
            checkIn: hotel?.checkIn || "-",
            checkOut: hotel?.checkOut || "-",
            occupancy: hotel?.occupancy || "-",
            supplier: hotel?.supplierText || "-",
            ratePlanName: "-",
            ratePlanCode: "-",
            roomType: hotel?.lowestRoomType || "-",
            baseRate: fallbackRate?.baseRate,
            subtotal: fallbackRate?.subtotal,
            tax: fallbackRate?.tax,
            fee: fallbackRate?.fee,
            total: fallbackRate?.totalRate,
            currency: hotel?.currency || "USD",
            refundable:
              fallbackRefundable === true
                ? "Yes"
                : fallbackRefundable === false
                  ? "No"
                  : "-",
            numberOfDays: hotel?.numberOfDays || "-",
            rating: hotel?.rating || "-",
            __hotel: hotel,
          },
        ];
      }

      return rates.map((rate) => {
        const refundableValue = parseRefundable(
          rate?.refundable ?? rate?.refundability,
        );
        const ratePlanName =
          String(rate?.ratePlanName || rate?.name || "-").trim() || "-";
        const ratePlanCode =
          String(rate?.ratePlanCode || rate?.id || "-").trim() || "-";
        const roomType = String(rate?.roomType || rate?.name || "-").trim() || "-";
        return {
          heroImage: hotel?.heroImage || "",
          id: hotel?.id || "-",
          name: hotel?.name || "-",
          checkIn: hotel?.checkIn || "-",
          checkOut: hotel?.checkOut || "-",
          occupancy: hotel?.occupancy || "-",
          supplier: rate?.providerName || hotel?.supplierText || "-",
          ratePlanName,
          ratePlanCode,
          roomType,
          baseRate: rate?.baseRate,
          subtotal: rate?.subtotal,
          tax: rate?.tax,
          fee: rate?.fee,
          total: rate?.totalRate,
          currency: rate?.currency || hotel?.currency || "USD",
          refundable:
            refundableValue === true ? "Yes" : refundableValue === false ? "No" : "-",
          numberOfDays: hotel?.numberOfDays || "-",
          rating: hotel?.rating || "-",
          __hotel: hotel,
        };
      });
    });
  }, [displayHotels, lowestPriceOnly, refundableFilter]);

  const exportRows = useMemo(
    () => buildExportRows(tableRows),
    [tableRows]
  );

  const hotelTableColumns = useMemo(
    () => [
      {
        name: "heroImage",
        label: "Image",
        options: {
          sort: false,
          filter: false,
          customBodyRenderLite: (dataIndex) => {
            const row = tableRows[dataIndex];
            if (!row?.heroImage) {
              return "-";
            }
            return (
              <img
                src={row.heroImage}
                alt={row.name || "hotel"}
                style={{
                  width: 52,
                  height: 52,
                  objectFit: "cover",
                  borderRadius: 4,
                }}
              />
            );
          },
        },
      },
      {
        name: "id",
        label: "Hotel ID",
        options: {
          customBodyRenderLite: (dataIndex) => {
            const row = tableRows[dataIndex];
            return (
              <Button
                size="small"
                variant="text"
                sx={{
                  minWidth: 0,
                  px: 0,
                  py: 0,
                  fontSize: "1.05rem",
                  fontWeight: 600,
                  textTransform: "none",
                }}
                onClick={() => {
                  setSelectedHotelJson(row?.__hotel || null);
                  setJsonDialogOpen(true);
                }}
              >
                {row?.id || "-"}
              </Button>
            );
          },
        },
      },
      {
        name: "name",
        label: "Hotel Name",
      },
      {
        name: "checkIn",
        label: "Check In",
      },
      {
        name: "checkOut",
        label: "Check Out",
      },
      {
        name: "occupancy",
        label: "Occupancy",
      },
      {
        name: "baseRate",
        label: "Base Rate",
        options: {
          customBodyRenderLite: (dataIndex) =>
            formatMoney(tableRows[dataIndex]?.baseRate),
        },
      },
      {
        name: "subtotal",
        label: "Subtotal",
        options: {
          customBodyRenderLite: (dataIndex) =>
            formatMoney(tableRows[dataIndex]?.subtotal),
        },
      },
      {
        name: "tax",
        label: "Tax",
        options: {
          customBodyRenderLite: (dataIndex) =>
            formatMoney(tableRows[dataIndex]?.tax),
        },
      },
      {
        name: "fee",
        label: "Fee",
        options: {
          customBodyRenderLite: (dataIndex) =>
            formatMoney(tableRows[dataIndex]?.fee),
        },
      },
      {
        name: "total",
        label: "Total",
        options: {
          customBodyRenderLite: (dataIndex) =>
            formatMoney(tableRows[dataIndex]?.total),
        },
      },
      {
        name: "refundable",
        label: "Refundable",
      },
      {
        name: "numberOfDays",
        label: "No Of Days",
      },
      {
        name: "rating",
        label: "Rating",
      },
    ],
    [tableRows]
  );

  const hotelTableOptions = useMemo(
    () => ({
      responsive: "standard",
      selectableRows: "none",
      filter: false,
      viewColumns: false,
      search: true,
      searchAlwaysOpen: true,
      print: false,
      download: false,
      rowsPerPage: 20,
      rowsPerPageOptions: [10, 20, 50],
      elevation: 0,
      textLabels: {
        body: {
          noMatch: "No hotels found for this selection.",
        },
      },
      setTableProps: () => ({
        style: {
          minWidth: "1400px",
        },
      }),
    }),
    []
  );

  const downloadAsCsv = () => {
    if (!exportRows.length) {
      toast.info("No rows available to export");
      return;
    }

    const filename = lowestPriceOnly
      ? `shop_tool_lowest_price_${Date.now()}.csv`
      : `shop_tool_${Date.now()}.csv`;

    downloadCsv(exportRows, filename);
  };

  const downloadAsExcel = () => {
    if (!exportRows.length) {
      toast.info("No rows available to export");
      return;
    }

    const filename = lowestPriceOnly
      ? `shop_tool_lowest_price_${Date.now()}.xlsx`
      : `shop_tool_${Date.now()}.xlsx`;

    downloadExcel(exportRows, filename);
  };

  return (
    <>
      <Helmet>
        <title>Sevenly-Shop Tool</title>
        <meta name="Shop Tool" content="Shop Tool" />
      </Helmet>

      <h1 className="mb--12 sevenly-cursor"> - </h1>
      <div className="d-flex justify-content-between mb-4">
        <h2>Shop Tool</h2>
      </div>

      <Paper
        elevation={2}
        sx={{
          p: 2.5,
          mb: 3,
          ...DASHBOARD_CONTROL_SX,
          "& .MuiFormControlLabel-label": {
            fontSize: "1.25rem",
            fontWeight: 500,
          },
        }}
      >
        <Stack spacing={2}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <FormControl fullWidth sx={DASHBOARD_CONTROL_SX}>
              <InputLabel id="member-type-label">Member Type</InputLabel>
              <Select
                labelId="member-type-label"
                label="Member Type"
                value={memberType}
                onChange={(event) => setMemberType(event.target.value)}
              >
                {MEMBER_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth sx={DASHBOARD_CONTROL_SX}>
              <InputLabel id="search-mode-label">Search Mode</InputLabel>
              <Select
                labelId="search-mode-label"
                label="Search Mode"
                value={searchMode}
                onChange={(event) => {
                  const mode = event.target.value;
                  setSearchMode(mode);
                  setShopResult(null);
                }}
              >
                <MenuItem value="location">Location</MenuItem>
                <MenuItem value="hotelId">Hotel ID</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Occupants"
              type="number"
              value={occupants}
              onChange={(event) => setOccupants(event.target.value)}
              fullWidth
              sx={DASHBOARD_CONTROL_SX}
              inputProps={{ min: 1 }}
            />

            <TextField
              label="Max Hotels"
              type="number"
              value={maxHotels}
              onChange={(event) => setMaxHotels(event.target.value)}
              fullWidth
              sx={DASHBOARD_CONTROL_SX}
              inputProps={{ min: 1, max: 1000 }}
            />
          </Stack>

          <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="center">
            <FormControlLabel
              control={
                <Switch
                  checked={multiDateMode}
                  onChange={(event) => {
                    const checked = event.target.checked;
                    setMultiDateMode(checked);
                    if (!checked) {
                      setDateRanges([{ checkIn, checkOut }]);
                    }
                  }}
                />
              }
              label="Multiple Dates"
            />

            {!multiDateMode ? (
              <>
                <TextField
                  label="Check In"
                  type="date"
                  value={checkIn}
                  onChange={(event) => setCheckIn(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={DASHBOARD_CONTROL_SX}
                  fullWidth
                />

                <TextField
                  label="Check Out"
                  type="date"
                  value={checkOut}
                  onChange={(event) => setCheckOut(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={DASHBOARD_CONTROL_SX}
                  fullWidth
                />
              </>
            ) : null}
          </Stack>

          {multiDateMode ? (
            <Stack spacing={1}>
              {dateRanges.map((range, index) => (
                <Stack key={`range-${index}`} direction={{ xs: "column", md: "row" }} spacing={1}>
                  <TextField
                    label={`Check In #${index + 1}`}
                    type="date"
                    value={range.checkIn}
                    onChange={(event) => {
                      const nextRanges = dateRanges.map((entry, entryIndex) =>
                        entryIndex === index
                          ? { ...entry, checkIn: event.target.value }
                          : entry
                      );
                      setDateRanges(nextRanges);
                    }}
                    InputLabelProps={{ shrink: true }}
                    sx={DASHBOARD_CONTROL_SX}
                    fullWidth
                  />

                  <TextField
                    label={`Check Out #${index + 1}`}
                    type="date"
                    value={range.checkOut}
                    onChange={(event) => {
                      const nextRanges = dateRanges.map((entry, entryIndex) =>
                        entryIndex === index
                          ? { ...entry, checkOut: event.target.value }
                          : entry
                      );
                      setDateRanges(nextRanges);
                    }}
                    InputLabelProps={{ shrink: true }}
                    sx={DASHBOARD_CONTROL_SX}
                    fullWidth
                  />

                  {dateRanges.length > 1 ? (
                    <Button
                      variant="outlined"
                      color="error"
                      sx={{ fontSize: "1.3rem", minWidth: 110 }}
                      onClick={() => {
                        const nextRanges = dateRanges.filter((_, entryIndex) => entryIndex !== index);
                        setDateRanges(nextRanges.length > 0 ? nextRanges : [{ checkIn, checkOut }]);
                      }}
                    >
                      Remove
                    </Button>
                  ) : null}
                </Stack>
              ))}

              <Box>
                <Button
                  variant="outlined"
                  sx={{ fontSize: "1.4rem", minWidth: 150 }}
                  onClick={() =>
                    setDateRanges([...dateRanges, { checkIn: "", checkOut: "" }])
                  }
                >
                  Add Date Range
                </Button>
              </Box>
            </Stack>
          ) : null}

          {searchMode === "location" ? (
            <Autocomplete
              options={locationOptions}
              value={selectedLocation}
              loading={loadingAutosuggest}
              getOptionLabel={buildAutosuggestLabel}
              isOptionEqualToValue={(option, value) =>
                String(option?.id || "") === String(value?.id || "")
              }
              onChange={(event, value) => {
                setSelectedLocation(value || null);
                if (!value) {
                  setShopResult(null);
                }
              }}
              onInputChange={(event, value, reason) => {
                if (reason === "input") {
                  setSearchTerm(value);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search Location (Autosuggest)"
                  placeholder="Type city, airport, state, hotel..."
                  sx={DASHBOARD_CONTROL_SX}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingAutosuggest ? (
                          <CircularProgress color="inherit" size={18} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          ) : (
            <TextField
              label="Hotel ID(s)"
              placeholder="Enter one or more hotel IDs, comma separated"
              value={hotelIdInput}
              onChange={(event) => setHotelIdInput(event.target.value)}
              sx={DASHBOARD_CONTROL_SX}
              fullWidth
            />
          )}

          <Box>
            <Button
              className="sevenly-btn text-white"
              onClick={executeShopSearch}
              disabled={loadingSearch}
              sx={{
                minWidth: 132,
                height: "42px !important",
                px: "14px !important",
                fontSize: "1.15rem",
                textTransform: "uppercase",
              }}
            >
              {loadingSearch ? "Loading..." : "Search"}
            </Button>
          </Box>
        </Stack>
      </Paper>

      {shopResult ? (
        <Paper
          elevation={1}
          sx={{
            p: 2,
            "& .MuiFormControlLabel-label": {
              fontSize: "1.2rem",
              fontWeight: 500,
            },
          }}
        >
          <Stack spacing={1} sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "2rem" }}>
              {shopResult?.location?.fullName ||
                (searchMode === "hotelId" ? "Hotel ID Search" : "Selected Location")}
            </Typography>
            <Typography variant="body2" sx={{ fontSize: "1.2rem" }}>
              Hotels returned: {shopResult?.hotelsReturned || 0} /{" "}
              {shopResult?.totalHotelsFound || 0}
            </Typography>
            <Typography variant="body2" sx={{ fontSize: "1.2rem" }}>
              Rows shown: {tableRows.length}
            </Typography>
            {Array.isArray(shopResult?.dateRanges) && shopResult.dateRanges.length > 0 ? (
              <Typography variant="body2" sx={{ fontSize: "1.2rem" }}>
                Date ranges: {shopResult.dateRanges.map((range) => `${range.checkIn} -> ${range.checkOut}`).join(" | ")}
              </Typography>
            ) : null}
          </Stack>

          <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }} alignItems="center">
            <FormControl sx={RESULT_FILTER_CONTROL_SX}>
              <InputLabel id="refundable-filter-label">Refundable</InputLabel>
              <Select
                size="small"
                labelId="refundable-filter-label"
                label="Refundable"
                value={refundableFilter}
                onChange={(event) => setRefundableFilter(event.target.value)}
              >
                {REFUNDABLE_FILTER_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControlLabel
              sx={{ ml: 0 }}
              control={
                <Switch
                  size="small"
                  checked={lowestPriceOnly}
                  onChange={(event) => setLowestPriceOnly(event.target.checked)}
                />
              }
              label="Lowest Price Only"
            />

            <Button
              className="sevenly-btn-white"
              startIcon={<DownloadIcon />}
              onClick={downloadAsCsv}
              sx={{
                minWidth: 150,
                height: "42px !important",
                px: "14px !important",
                fontSize: "1.05rem",
                textTransform: "uppercase",
                color: "#2366c6 !important",
                border: "1px solid #2366c6 !important",
              }}
            >
              Download CSV
            </Button>
            <Button
              className="sevenly-btn text-white"
              startIcon={<DownloadIcon />}
              onClick={downloadAsExcel}
              sx={{
                minWidth: 150,
                height: "42px !important",
                px: "14px !important",
                fontSize: "1.05rem",
                textTransform: "uppercase",
              }}
            >
              Download Excel
            </Button>
          </Stack>

          <Box
            sx={{
              "& .MuiTableCell-root": {
                fontSize: "1.1rem",
                paddingTop: "10px",
                paddingBottom: "10px",
              },
              "& .MuiTableHead-root .MuiTableCell-root": {
                fontSize: "1.1rem",
                fontWeight: 700,
              },
            }}
          >
            <MUIDataTable
              title=""
              data={tableRows}
              columns={hotelTableColumns}
              options={hotelTableOptions}
            />
          </Box>
        </Paper>
      ) : null}

      <Dialog
        open={jsonDialogOpen}
        onClose={() => setJsonDialogOpen(false)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle sx={{ fontSize: "2.6rem", fontWeight: 600 }}>
          {selectedHotelJson?.name || "Hotel Details"}
          {selectedHotelJson?.id ? ` (${selectedHotelJson.id})` : ""}
        </DialogTitle>
        <DialogContent dividers>
          {selectedHotelJson ? (
            <Stack spacing={2}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                {selectedHotelJson?.heroImage ? (
                  <Box
                    component="img"
                    src={selectedHotelJson.heroImage}
                    alt={selectedHotelJson?.name || "hotel"}
                    sx={{
                      width: { xs: "100%", md: 260 },
                      height: { xs: 180, md: 180 },
                      objectFit: "cover",
                      borderRadius: 1,
                    }}
                  />
                ) : null}
                <Stack spacing={0.5} sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: "2.4rem" }}>
                    {selectedHotelJson?.name || "-"}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: "1.6rem" }}>
                    Hotel ID: {selectedHotelJson?.id || "-"}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: "1.6rem" }}>
                    Member Type: {selectedHotelJson?.memberType || "-"}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: "1.6rem" }}>
                    Rating: {selectedHotelJson?.rating || "-"}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: "1.6rem" }}>
                    Stay: {selectedHotelJson?.checkIn || "-"} to{" "}
                    {selectedHotelJson?.checkOut || "-"} ({selectedHotelJson?.numberOfDays || "-"} day(s))
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: "1.6rem" }}>
                    Occupancy: {selectedHotelJson?.occupancy || "-"}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: "1.6rem" }}>
                    Provider: {selectedHotelJson?.provider?.providerName || "-"} (
                    {selectedHotelJson?.provider?.providerId || "-"})
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: "1.6rem" }}>
                    Provider Hotel ID: {selectedHotelJson?.provider?.providerHotelId || "-"}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: "1.6rem" }}>
                    Suppliers:{" "}
                    {Array.isArray(selectedHotelJson?.suppliers) &&
                    selectedHotelJson.suppliers.length > 0
                      ? selectedHotelJson.suppliers.join(", ")
                      : "-"}
                  </Typography>
                </Stack>
              </Stack>

              <TableContainer component={Paper} variant="outlined">
                <Table
                  size="small"
                  sx={{
                    "& .MuiTableCell-root": {
                      fontSize: "1.6rem",
                    },
                    "& .MuiTableHead-root .MuiTableCell-root": {
                      fontSize: "1.65rem",
                      fontWeight: 700,
                    },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell colSpan={6}>Summary</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>Init Total</TableCell>
                      <TableCell>{formatMoney(selectedHotelJson?.initRate?.totalRate)}</TableCell>
                      <TableCell>Init Per Night</TableCell>
                      <TableCell>{formatMoney(selectedHotelJson?.initRate?.totalRatePerNight)}</TableCell>
                      <TableCell>Init Discount</TableCell>
                      <TableCell>{formatMoney(selectedHotelJson?.initRate?.discountPrice)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Lowest Room</TableCell>
                      <TableCell>{selectedHotelJson?.lowestRoomSummary?.roomType || "-"}</TableCell>
                      <TableCell>Lowest Total</TableCell>
                      <TableCell>{formatMoney(selectedHotelJson?.lowestRoomSummary?.totalRate)}</TableCell>
                      <TableCell>Lowest Refundable</TableCell>
                      <TableCell>
                        {selectedHotelJson?.lowestRoomSummary?.refundable === true
                          ? "Yes"
                          : selectedHotelJson?.lowestRoomSummary?.refundable === false
                            ? "No"
                            : "-"}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: "2rem" }}>
                Rates
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
                <Table
                  size="small"
                  stickyHeader
                  sx={{
                    "& .MuiTableCell-root": {
                      fontSize: "1.5rem",
                    },
                    "& .MuiTableHead-root .MuiTableCell-root": {
                      fontSize: "1.55rem",
                      fontWeight: 700,
                    },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Board</TableCell>
                      <TableCell>Currency</TableCell>
                      <TableCell>Base</TableCell>
                      <TableCell>Subtotal</TableCell>
                      <TableCell>Tax</TableCell>
                      <TableCell>Fee</TableCell>
                      <TableCell>Total</TableCell>
                      <TableCell>Refundable</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(Array.isArray(selectedHotelJson?.rates) && selectedHotelJson.rates.length > 0
                      ? selectedHotelJson.rates
                      : Array.isArray(selectedHotelJson?.roomsAndRates)
                        ? selectedHotelJson.roomsAndRates
                        : []
                    ).map((rate, index) => (
                      <TableRow key={`${rate?.id || "rate"}-${index}`}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{rate?.name || rate?.roomType || "-"}</TableCell>
                        <TableCell>{rate?.type || "-"}</TableCell>
                        <TableCell>{rate?.boardBasis || "-"}</TableCell>
                        <TableCell>{rate?.currency || "-"}</TableCell>
                        <TableCell>{formatMoney(rate?.baseRate)}</TableCell>
                        <TableCell>{formatMoney(rate?.subtotal)}</TableCell>
                        <TableCell>{formatMoney(rate?.tax)}</TableCell>
                        <TableCell>{formatMoney(rate?.fee)}</TableCell>
                        <TableCell>{formatMoney(rate?.totalRate)}</TableCell>
                        <TableCell>
                          {rate?.refundable === true
                            ? "Yes"
                            : rate?.refundable === false
                              ? "No"
                              : "-"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
          ) : (
            <Typography variant="body2" sx={{ fontSize: "1.6rem" }}>
              No hotel selected.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            className="sevenly-btn text-white"
            sx={{ minWidth: 120 }}
            onClick={() => setJsonDialogOpen(false)}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ShopTool;
